/* eslint-disable no-underscore-dangle */
import Backbone from 'backbone';
import $ from 'jquery';
import { delay, debounce, extend, result, each, isUndefined, has } from 'underscore';
import { ResizeObserver as ResizeObserverPollyFill } from '@juggle/resize-observer';
import template from './template.pug';

// [TODO] Need check Mac OS
const isFirefox = typeof InstallTrigger !== 'undefined';
const scrollbarWidth = isFirefox ? 17 : 12;
const overlapClass = 'overlap-shadow';

class ScrollbarView extends Backbone.View {
  initialize(options) {
    this.freezingSelector = options.freezingSelector;
    this.otherSelector = options.otherSelector;
    this.scrollbarParentSelector = options.scrollbarParentSelector;
    this.calculateOtherHorizontalOffset = options.calculateOtherHorizontalOffset;
  }

  addEvent() {
    const $bar = this.$el.find(this.scrollbarParentSelector);
    $bar.on('scroll', () => {
      this.calculateOtherHorizontalOffset();
    });
  }

  render() {
    const parentStyle = {
      'z-index': 10,
      height: `${scrollbarWidth}px`,
      'overflow-y': 'hidden',
      'overflow-x': 'scroll',
    };
    const childStyle = {
      height: `${scrollbarWidth}px`,
    };
    this.$el.html(template({ parentStyle, childStyle }));

    this.addEvent();
    return this;
  }
}

export const projectionGridScrollbarPlugin = definePlugin => definePlugin('projectionGridScrollbar', [
  'gridView',
  'config',
], (gridView, config) => {
  const pluginConfig = result(config.plugins, 'projectionGridScrollbar', {});
  const {
    pageContainer = '.table-page-container',
    tableSelector = '.table-container',
    rowSelector = '.flex-row',
    cellSelector = '.flex-cell',
    freezingGroupSelector = '.freezing-group-container',
    freezingSelector = '.freezing-group',
    otherGroupSelector = '.other-group-container',
    otherSelector = '.other-group',
    scrollbarContainerSelector = '.scrollbar.flex-row',
    scrollbarParentSelector = '.bar-parent',
    scrollbarChildSelector = '.bar-child',
    scrollElementSelector = null,
    shellSelector = '.app-shell-content',
  } = pluginConfig;

  const getBarParent = () => $(scrollbarParentSelector);
  const getBarChild = () => $(scrollbarChildSelector);
  const scrollOtherGroupToLeft = () => $(otherGroupSelector).scrollLeft(0);
  const setOverlapClass = isOverLap => (isOverLap ?
    $(otherGroupSelector).addClass(overlapClass) :
    $(otherGroupSelector).removeClass(overlapClass));

  const $window = $(window);
  let prevOffsetWillEditing = 0;
  let prevOffset = 0;

  function calculateParentWidth() {
    const $freezing = $(freezingGroupSelector);
    const $table = $(tableSelector);
    const right = $table.width() - $freezing.width();
    getBarParent().css({ width: right });
  }

  function calculateChildWidth() {
    const width = $(otherSelector).width();
    getBarChild().css({ width });
  }

  // After grid reload, need restore the scrollbar's scrolling offset
  function setHorizontalOffset(offset, transition = false) {
    $(otherSelector).css({
      'transition-duration': transition ? '0.3s' : '0s',
      transform: `translate(-${offset}px, 0)`,
    });
    getBarParent().scrollLeft(offset);
    setOverlapClass(prevOffset > 0);
    prevOffset = offset;
  }

  function calculateShowOrHide() {
    const $barParent = getBarParent();
    const $row = $(tableSelector).find(rowSelector).eq(0);
    const rowWidth = $row.width();
    const freezWidth = $row.find(freezingGroupSelector).width();
    const otherWidth = $row.find(otherSelector).width();

    if (rowWidth >= freezWidth + otherWidth) {
      // Hide scrollbar. Reset the css translate
      $barParent.css({ opacity: 0 });
      setHorizontalOffset(0);
    } else {
      // Show scrollbar
      $barParent.css({ opacity: 1 });
    }
  }

  function calculateScrollbarPosition() {
    const $freezing = $(freezingGroupSelector);
    const $other = $(otherGroupSelector);
    const $barContainer = $(scrollbarContainerSelector);
    const windowHeight = $window.height();
    const barContainerTopOffset = result($barContainer.offset(), 'top', 0);
    const style = { bottom: '0', left: 0 };
    const shellWidth = $(shellSelector).width();
    const pageWidth = $(pageContainer).width();
    let bottom = 0;
    if (shellWidth < pageWidth) {
      bottom = -16;
    }

    if (barContainerTopOffset > windowHeight) {
      const left = result($other.offset(), 'left');
      extend(style, {
        position: 'fixed',
        transform: `translate(${left}px, ${bottom}px)`,
      });
    } else {
      const left = $freezing.width();
      extend(style, {
        position: 'relative',
        transform: `translate(${left}px, 0)`,
      });
    }

    getBarParent().css(style);
  }

  function calculateOtherHorizontalOffset() {
    const left = getBarParent().scrollLeft();
    setHorizontalOffset(left);
    setOverlapClass(left > 0);

    if (scrollElementSelector) {
      const scrollElement = $(scrollElementSelector);
      scrollElement.css('transform', `translate(${-(left - prevOffsetWillEditing)}px, 0)`);
    }
  }


  function calculateFocusElementOffset(e) {
    delay(() => {
      const childElement = $(e.target).closest(cellSelector);
      const parentElement = childElement.closest(otherGroupSelector);
      const cRect = childElement[0].getBoundingClientRect();
      const pRect = parentElement[0].getBoundingClientRect();

      // [x]{[ ][ ][ ]} the focused cell is left of the parent view. Scroll to right
      // {[ ][ ][ ]}[x] the focused cell is left of the parent view. Scroll to left
      // {[ ][x][ ][ ]} the focused cell is inside of the parent view. No need scroll
      let offsetDelta;
      if (cRect.left < pRect.left) {
        offsetDelta = cRect.left - pRect.left;
      } else if (cRect.left + cRect.width > pRect.left + pRect.width) {
        offsetDelta = (cRect.left + cRect.width) - (pRect.left + pRect.width);
      } else {
        offsetDelta = 0;
      }
      setHorizontalOffset(prevOffset + offsetDelta, true);
    }, 50);
  }

  // This event can be triggered by Tab key
  // Need handle scroll event
  // Since the scroll event does not bubble up, need bind the event on the element directly
  function addScrollRowEvent() {
    const otherGroupElements = gridView.$(otherGroupSelector).toArray();
    each(otherGroupElements, (el) => {
      const scrollEvent = result($._data(el, 'events'), 'scroll');
      if (isUndefined(scrollEvent)) {
        $(el).on('scroll', scrollOtherGroupToLeft);
      }
    });

    const focusElements = gridView.$(`${otherGroupSelector} a, ${otherGroupSelector} [tabindex=0]`).toArray();
    each(focusElements, (el) => {
      const scrollEvent = result($._data(el, 'events'), 'focus');
      if (isUndefined(scrollEvent)) {
        $(el).on('focus', calculateFocusElementOffset);
      }
    });
  }

  function horizontalScrollHandler(e) {
    const isMouseInside = $(e.target).parents(`${otherGroupSelector}, ${freezingGroupSelector}`).length > 0;
    const isShowScrollbar = getBarParent().css('opacity') === '1';

    if (!isMouseInside || !isShowScrollbar) return;

    let offset;
    if (e.shiftKey) {
      offset = prevOffset + e.originalEvent.deltaY;
    } else if (Math.abs(e.originalEvent.deltaX) > 0) {
      offset = prevOffset + e.originalEvent.deltaX;
    } else {
      return;
    }

    const cWidth = $(otherSelector).width();
    const pWidth = $(otherGroupSelector).width();
    offset = Math.max(0, offset);
    offset = Math.min(cWidth - pWidth, offset);

    setHorizontalOffset(offset);
    e.preventDefault();
  }

  function addHorizontalScrollEvent() {
    const el = document.querySelector(tableSelector);
    if (el) {
      const scrollEvent = result($._data(el, 'events'), 'wheel');
      if (isUndefined(scrollEvent)) {
        $(el).on('wheel', horizontalScrollHandler);
      }
    }
  }

  // Add Scrollbar View into the grid footer
  const scrollbarView = new ScrollbarView({
    freezingSelector,
    otherSelector,
    scrollbarParentSelector,
    calculateOtherHorizontalOffset,
  }).render();

  gridView.prependFootRows([{
    classes: ['noborder', 'scrollbar'],
    view: scrollbarView,
  }]);

  // Listen event
  gridView.on('didUpdate', () => {
    calculateParentWidth();
    calculateChildWidth();
    calculateShowOrHide();
    calculateScrollbarPosition();
  });

  gridView.on('didRedrawHeader', () => {
    setHorizontalOffset(prevOffset);
  });

  gridView.on('didRedrawFooter', () => {
    setHorizontalOffset(prevOffset);
  });

  // grid body did redraw
  const lazyAddScrollRowEvent = debounce(addScrollRowEvent, 500);
  gridView.on('didRedrawBody', () => {
    setHorizontalOffset(prevOffset);
    lazyAddScrollRowEvent();
    addHorizontalScrollEvent();
  });

  // save the offset. this value will help to scroll the inline editing popup
  gridView.on('willEdit', () => {
    prevOffsetWillEditing = getBarParent().scrollLeft();
  });
  gridView.on('willShowLabelsPopup', () => {
    prevOffsetWillEditing = getBarParent().scrollLeft();
  });

  // scroll the vertical scrollbar
  const lazycalculateScrollbarPosition = debounce(calculateScrollbarPosition, 100);
  $(pageContainer).scroll(() => {
    lazycalculateScrollbarPosition();
  });

  // scroll horzatial bar of page container's parent
  $(pageContainer).parent().on('scroll', () => {
    lazycalculateScrollbarPosition();
  });

  // TODO remove event handler when the gridview was destroyed.
  // Resize the able-page-container.
  // can be triggered by resizing window, expand/collapse table, hide/show main menu.
  const lazyResizeCalculate = debounce(() => {
    calculateParentWidth();
    calculateChildWidth();
    calculateShowOrHide();
  }, 100);

  if (has(window, 'ResizeObserver')) {
    new ResizeObserver(lazyResizeCalculate).observe(document.querySelector(pageContainer));
  } else {
    new ResizeObserverPollyFill(lazyResizeCalculate).observe(document.querySelector(pageContainer));
  }
});
